import { useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/electron/renderer'

import { useIsTurnLive } from '@renderer/chat/store'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { findHistoryChatRow } from '@renderer/overlay/hooks/conversation-preview-cache'
import { useConversationPreviewAcknowledgement } from '@renderer/overlay/hooks/use-conversation-preview-acknowledgement'
import type { SidepanelChat } from '@renderer/overlay/hooks/running-chat-selection'
import { useForYouChatsFeed } from '@renderer/overlay/hooks/use-running-chats'
import { useHistoryActivity } from '@renderer/overlay/Sidepanel/hooks/use-history-activity'
import { isActivityUnread } from '@renderer/overlay/Sidepanel/utils/activity-unread'
import { useViewNavigation } from '@renderer/overlay/Sidepanel/view-atoms'
import { viewContextOf } from '@renderer/overlay/Sidepanel/view-definitions'

interface VisibleChatPreviewOptions {
  conversationId: string | undefined
  entranceSettled: boolean
  hasMessages: boolean
}

interface RenderedChatPreviewOptions extends VisibleChatPreviewOptions {
  isVisible: boolean
  acknowledgementChat?: SidepanelChat
}

export function useVisibleChatPreview({
  conversationId,
  entranceSettled,
  hasMessages,
}: VisibleChatPreviewOptions): void {
  const sidepanelState = useSidepanelState()
  const { topView } = useViewNavigation()
  const topChatId = viewContextOf(topView, 'chat')?.chatId
  const isVisible =
    sidepanelState === 'open' &&
    topView.id === 'chat' &&
    (!topChatId || !conversationId || topChatId === conversationId)
  useRenderedChatPreview({ conversationId, entranceSettled, hasMessages, isVisible })
}

export function useRenderedChatPreview({
  conversationId,
  entranceSettled,
  hasMessages,
  isVisible,
  acknowledgementChat,
}: RenderedChatPreviewOptions): void {
  const { data: chats } = useForYouChatsFeed()
  const { acknowledgePreview } = useConversationPreviewAcknowledgement()
  const isLocallyStreaming = useIsTurnLive(conversationId)
  const attemptedRef = useRef<string>()
  const listedChat = useMemo(() => chats.find((candidate) => candidate.id === conversationId), [chats, conversationId])
  const recentsChat = useUnreadRecentsChat(conversationId)
  const chat = acknowledgementChat ?? listedChat ?? recentsChat

  useEffect(() => {
    if (!chat || chat.isStreaming || isLocallyStreaming || !entranceSettled || !hasMessages || !isVisible) {
      return
    }

    const attemptKey = `${chat.id}:${chat.previewRevision}`
    if (attemptedRef.current === attemptKey) {
      return
    }
    attemptedRef.current = attemptKey
    void acknowledgePreview({ chat, disposition: 'seen' }).catch((error) => {
      if (attemptedRef.current === attemptKey) {
        attemptedRef.current = undefined
      }
      Sentry.captureException(error, { extra: { conversationId: chat.id, previewRevision: chat.previewRevision } })
    })
  }, [acknowledgePreview, chat, entranceSettled, hasMessages, isLocallyStreaming, isVisible])
}

/**
 * The acknowledgement target for a chat that never reaches For You, an ordinary conversation with
 * no outstanding action. Without it Recents raises its unread dot on every chat and never lowers it.
 */
function useUnreadRecentsChat(conversationId: string | undefined): SidepanelChat | undefined {
  const queryClient = useQueryClient()
  // Subscribing to the unfiltered feed loads it in windows that render no Recents of their own, and
  // re-renders this hook as it changes. The History view's per-filter caches are searched after it.
  const { items } = useHistoryActivity({})

  return useMemo(() => {
    if (!conversationId) return undefined
    const item =
      items.find((candidate) => candidate.type === 'chat' && candidate.id === conversationId) ??
      findHistoryChatRow(queryClient, conversationId)
    if (!item || item.previewRevision === undefined || !isActivityUnread(item)) return undefined
    return {
      id: item.id,
      title: item.title,
      conversationType: 'chat',
      // A `seen` ack retires no artifacts, so the ids Recents doesn't carry go unused.
      stagedActionIds: [],
      reminderIds: [],
      isStreaming: false,
      previewRevision: item.previewRevision,
      isUnread: true,
      updatedAt: item.unifiedUpdatedAt.toISOString(),
    }
  }, [conversationId, items, queryClient])
}
